/**
 * Small building blocks in the Gap Assessor look (white cards on a slate page, a header strip with an icon tile).
 * Loading never reads as a finding or an all-clear (design 10.1): a loading panel says it is loading, an empty
 * one says what was counted, and a failed one says so and offers to try again.
 *
 * PROVENANCE: the card, header and empty-state look of C:\Gap-Assessor\src\App.tsx and GapAssessmentHub.tsx
 * @ 2023d32; the chip, empty-state and own-scroll table primitives after ALEXANDER's components/ui/* @ 0e341f4
 * (BandChip, Empty, Table), re-skinned. Chips carry an icon shape as well as a colour (design 10.1).
 */

import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Hourglass, type LucideIcon } from 'lucide-react'

import { useLanguage, useT } from '../i18n/LanguageProvider'
import { ApiError } from '../lib/api'
import { BADGES, MARKS, type BadgeFamily, type BadgeSpec, type BadgeStatus, type MarkName } from '../lib/badges'
import type { ChipTone, StateChipSpec } from '../lib/cycle'
import { errorText } from '../lib/errors'
import type { Loaded as LoadState } from '../lib/useLoad'

export function Page({ title, subtitle, actions, back, children }: { title: string; subtitle?: ReactNode; actions?: ReactNode; back?: ReactNode; children: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
      {back && <div className="mb-2 text-sm">{back}</div>}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight break-words text-ol-text">{title}</h1>
          {subtitle && <div className="mt-1 text-ol-text-2">{subtitle}</div>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  )
}

export function Card({ title, children, aside, id }: { title?: ReactNode; children: ReactNode; aside?: ReactNode; id?: string }) {
  return (
    <section id={id} className="min-w-0 rounded-lg border border-ol-border bg-ol-card p-4 shadow-sm sm:p-5">
      {(title || aside) && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {title && <h2 className="text-lg font-semibold text-ol-text">{title}</h2>}
          {aside}
        </div>
      )}
      {children}
    </section>
  )
}

export function Loading() {
  const t = useT()
  return (
    <p className="flex items-center gap-2 text-ol-text-2" role="status">
      <Hourglass size={16} aria-hidden="true" />
      {t('common.loading')}
    </p>
  )
}

/** Whether asking again could change the answer: not for a refusal (a 4xx), which says the same the second time. */
export function retryable(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status >= 500 || error.status === 408 || error.status === 429
}

/**
 * An error as a person reads it, in the interface language (batch O-D, OLV-07): the refusal's code with its
 * parameters (lib/errors.ts), or for a failure to reach the server a fixed sentence. The server's English sentence and
 * any technical detail are never shown: the server logs them. A refusal offers no "try again".
 */
export function ErrorText({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t, language } = useLanguage()
  return (
    <div className="rounded-md border border-ol-red/30 bg-ol-red-soft px-3 py-2 text-ol-red" role="alert">
      <p>{errorText(error, t, language)}</p>
      {onRetry && retryable(error) && (
        <button type="button" onClick={onRetry} className="mt-1 text-sm underline">
          {t('common.retry')}
        </button>
      )}
    </div>
  )
}

/**
 * The three honest states of a read: loading, failed (with a retry), or the data. A panel's empty state is the
 * child's to say, because only the child knows what was counted.
 */
export function Show<T>({ load, children }: { load: LoadState<T>; children: (data: T) => ReactNode }) {
  if (load.error) return <ErrorText error={load.error} onRetry={load.reload} />
  if (load.data === null) return <Loading />
  return <>{children(load.data)}</>
}

/**
 * A section that is not built yet: says so in words and shows nothing invented. It names no build phase or decision
 * code (expert review FAM-04): those mean nothing to the person reading; the design documents keep them.
 */
export function LaterNote({ text }: { text: string }) {
  const t = useT()
  return (
    <div className="rounded-md border border-dashed border-ol-border bg-ol-page px-3 py-2 text-ol-text-2">
      <span className="mr-2 rounded bg-ol-action-soft px-1.5 py-0.5 text-sm font-medium text-ol-action-dark">{t('later.badge')}</span>
      {text}
    </div>
  )
}

/** Nothing to show, said plainly, with what was looked at. */
export function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-md border border-dashed border-ol-border bg-ol-page px-3 py-2 text-ol-text-2">{children}</p>
}

/** A short message after an act. */
export function Notice({ children, tone = 'green' }: { children: ReactNode; tone?: 'green' | 'amber' }) {
  const cls = tone === 'green' ? 'border-ol-green/30 bg-ol-green-soft text-ol-green' : 'border-ol-amber/40 bg-ol-amber-soft text-ol-amber'
  return (
    <p className={`rounded-md border px-3 py-2 ${cls}`} role="status">
      {children}
    </p>
  )
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled,
  ariaLabel,
  describedBy,
}: {
  children: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  variant?: 'primary' | 'secondary' | 'quiet' | 'danger'
  disabled?: boolean
  ariaLabel?: string
  /** The id of the words that say why the act is closed, read with the button. */
  describedBy?: string
}) {
  const styles = {
    primary: 'bg-ol-action text-white hover:bg-ol-action-dark',
    secondary: 'border border-ol-border bg-white text-ol-text hover:bg-ol-page',
    quiet: 'text-ol-action-dark underline hover:text-ol-action',
    danger: 'border border-ol-red/40 bg-white text-ol-red hover:bg-ol-red-soft',
  }[variant]
  return (
    <button type={type} onClick={onClick} disabled={disabled} aria-label={ariaLabel} aria-describedby={describedBy} className={`rounded-md px-3 py-2 text-[15px] font-medium disabled:opacity-50 ${styles}`}>
      {children}
    </button>
  )
}

/** A link styled as a button (navigation is a link, an act is a button). */
export function LinkButton({ to, children, variant = 'secondary' }: { to: string; children: ReactNode; variant?: 'primary' | 'secondary' }) {
  const styles = variant === 'primary' ? 'bg-ol-action text-white hover:bg-ol-action-dark' : 'border border-ol-border bg-white text-ol-text hover:bg-ol-page'
  return (
    <Link to={to} className={`inline-block rounded-md px-3 py-2 text-[15px] font-medium ${styles}`}>
      {children}
    </Link>
  )
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-sm font-medium text-ol-text-2">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-sm text-ol-text-3">{hint}</span>}
    </label>
  )
}

export const inputClass = 'w-full min-w-0 rounded-md border border-ol-border bg-white px-3 py-2 text-[15px] text-ol-text focus:border-ol-action'

/** A table in its own horizontal scroll container, so the page never scrolls sideways. */
export function Table({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <div className="overflow-x-auto" role={label ? 'region' : undefined} aria-label={label} tabIndex={label ? 0 : undefined}>
      <table className="w-full min-w-[32rem] border-collapse text-left text-[15px]">{children}</table>
    </div>
  )
}

export const thClass = 'py-2 pr-3 text-sm font-medium text-ol-text-2'
export const tdClass = 'py-2 pr-3 align-top'
export const trClass = 'border-b border-ol-border last:border-0'

const TONES: Record<ChipTone, string> = {
  red: 'border-ol-red/30 bg-ol-red-soft text-ol-red',
  amber: 'border-ol-amber/40 bg-ol-amber-soft text-ol-amber',
  green: 'border-ol-green/30 bg-ol-green-soft text-ol-green',
  blue: 'border-ol-blue/30 bg-white text-ol-blue',
  neutral: 'border-ol-border bg-ol-page text-ol-text-2',
}

/**
 * A state as a chip: a word, a shape and a colour, so it never depends on colour alone (design 10.1). The icon is
 * aria-hidden; the word carries the meaning. A status of a family takes its icon from the registry (lib/badges.ts),
 * through Badge; a one-off chip names a mark.
 */
export function Chip({ tone, icon = 'none', wrap = false, children }: { tone: ChipTone; icon?: MarkName | LucideIcon; wrap?: boolean; children: ReactNode }) {
  const Icon = typeof icon === 'string' ? MARKS[icon] : icon
  // A chip that holds a phrase rather than a word may wrap, so it never reaches past a phone's screen.
  return (
    <span className={`inline-flex items-center gap-1 ${wrap ? 'max-w-full' : 'whitespace-nowrap'} rounded border px-1.5 py-0.5 text-sm ${TONES[tone]}`}>
      <Icon size={14} aria-hidden="true" className="shrink-0" />
      {children}
    </span>
  )
}

/**
 * A status of a family as a chip: the family's icon and word for it (lib/badges.ts). `children` says more in the
 * word's place (a date, a name), never something else; the tone stays the caller's.
 */
export function Badge<F extends BadgeFamily>({ family, status, tone, wrap = false, children }: { family: F; status: BadgeStatus<F>; tone: ChipTone; wrap?: boolean; children?: ReactNode }) {
  const t = useT()
  const b = (BADGES[family] as Record<string, BadgeSpec | undefined>)[status]
  // A status the registry does not know (an answer newer than this screen) still shows, by its code.
  if (!b) return <Chip tone={tone} wrap={wrap}>{children ?? status}</Chip>
  return (
    <Chip tone={tone} icon={b.icon} wrap={wrap}>
      {children ?? t(b.word)}
    </Chip>
  )
}

export function StateChips({ chips }: { chips: StateChipSpec[] }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {chips.map((c) => (
        <Badge key={c.status} family="occurrence" status={c.status} tone={c.tone} />
      ))}
    </span>
  )
}

const SEVERITY_TONE: Record<string, ChipTone> = { critical: 'red', high: 'red', medium: 'amber', low: 'neutral' }

export function SeverityChip({ severity }: { severity: string }) {
  return <Badge family="severity" status={severity as BadgeStatus<'severity'>} tone={SEVERITY_TONE[severity] ?? 'neutral'} />
}

/** A choice of one view or window: pressed buttons, keyboard-reachable, one control for one thing. */
export function Segmented<V extends string>({ label, value, options, onChange }: { label: string; value: V; options: { value: V; label: string }[]; onChange: (v: V) => void }) {
  return (
    <div role="group" aria-label={label} className="inline-flex flex-wrap overflow-hidden rounded-md border border-ol-border">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1 text-[15px] ${value === o.value ? 'bg-ol-action text-white' : 'bg-white text-ol-text hover:bg-ol-page'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/**
 * An act that needs a reason or a note: the button opens a small form in place (no browser prompt, which cannot
 * be translated or read well by a screen reader).
 */
export function InlineReason({
  buttonLabel,
  fieldLabel,
  submitLabel,
  onSubmit,
  optional = false,
  variant = 'secondary',
  minLength = 3,
  warning,
}: {
  buttonLabel: string
  fieldLabel: string
  submitLabel: string
  onSubmit: (text: string) => Promise<boolean>
  optional?: boolean
  variant?: 'primary' | 'secondary' | 'quiet' | 'danger'
  minLength?: number
  /** What follows the act, said before the person confirms it (an act that cannot be undone). */
  warning?: string
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  if (!open) return <Button variant={variant} onClick={() => setOpen(true)}>{buttonLabel}</Button>
  return (
    <form
      className="w-full space-y-2 rounded-md border border-ol-border bg-ol-page p-3"
      onSubmit={(e) => {
        e.preventDefault()
        setBusy(true)
        void onSubmit(text.trim()).then((ok) => {
          setBusy(false)
          if (ok) {
            setOpen(false)
            setText('')
          }
        })
      }}
    >
      {warning && <Notice tone="amber">{warning}</Notice>}
      <Field label={fieldLabel}>
        <textarea className={inputClass} rows={2} value={text} onChange={(e) => setText(e.target.value)} required={!optional} minLength={optional ? undefined : minLength} autoFocus />
      </Field>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={busy}>{submitLabel}</Button>
        <Button variant="secondary" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
      </div>
    </form>
  )
}

/** Terms and their values, two columns on a wide screen and one on a phone. */
export function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[minmax(10rem,auto)_1fr]">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-ol-text-2">{k}</dt>
          <dd className="min-w-0 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  )
}

/** The guidance under every free-text field of a register (design 5.9, 7.1). */
export function NoCustomerNames() {
  const t = useT()
  return <span className="mt-1 block text-sm text-ol-text-3">{t('common.noCustomerNames')}</span>
}
